import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Radio,
  RadioGroup,
  Text,
  Divider,
} from "@chakra-ui/react";
import { CalendarIcon } from "@chakra-ui/icons";
import { FaUser, FaShip } from "react-icons/fa";
import Translate from "../../LanguageTranslation/Translate";
import axiosInstance from "../../../helpers/axiosInstance";

const BRAND = "#286328";
const BRAND_FILL = "rgba(40,99,40,0.10)";

const BOAT_TYPES = [
  { key: "cruise", label: "Cruise" },
  { key: "liveaboard", label: "Liveaboard" },
  { key: "yacht", label: "Yacht" },
];

const CounterField = ({ icon, label, value, dec, inc }) => (
  <Flex align="center" mb={6}>
    <HStack w="220px" spacing={3}>
      <Icon as={icon} />
      <Translate text={label} />
    </HStack>

    <HStack spacing={8}>
      <IconButton
        aria-label="minus"
        icon={
          <Text as="span" fontWeight="bold">
            −
          </Text>
        }
        size="sm"
        variant="ghost"
        border="1px solid"
        borderColor={BRAND}
        color={BRAND}
        onClick={dec}
        isDisabled={value === 1}
        _hover={{ bg: "transparent" }}
      />
      <Text w="20px" textAlign="center" mb={0}>
        {value}
      </Text>
      <IconButton
        aria-label="plus"
        icon={
          <Text as="span" fontWeight="bold">
            ＋
          </Text>
        }
        size="sm"
        variant="ghost"
        border="1px solid"
        borderColor={BRAND}
        color={BRAND}
        onClick={inc}
        _hover={{ bg: "transparent" }}
      />
    </HStack>
  </Flex>
);

const BoatTripDetails = ({
  boatType,
  setBoatType,
  boatDays,
  setBoatDays,
  boatPeople,
  setBoatPeople,
  boatResult,
  setBoatResult,
  selectedTrip,
  hasTrips,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCalculate = async () => {
    // Check if trip is selected / any trips exist
    if (!selectedTrip || selectedTrip.trim() === '') {
      alert(hasTrips
        ? 'Please select a trip before calculating emissions.'
        : 'No trips found. Please create a trip plan first, then select it before calculating emissions.'
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        boat_type: boatType,
        number_of_days: boatDays,
        number_of_people: boatPeople,
        environmental_cause: "reforestation", // Default cause
      };

      const response = await axiosInstance.post(
        "/co2-emissions/calculate/boat",
        payload
      );

      setBoatResult(response.data);
    } catch (err) {
      console.error("Error calculating boat emissions:", err);
      setError(err.response?.data?.error || "Failed to calculate emissions");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* vessel type */}
      <RadioGroup value={boatType} onChange={setBoatType} mb={10}>
        <HStack spacing={10}>
          {BOAT_TYPES.map((b) => (
            <Radio
              key={b.key}
              value={b.key}
              colorScheme="green"
              _checked={{ bg: BRAND_FILL, borderColor: BRAND }}
            >
              <Translate text={b.label} />
            </Radio>
          ))}
        </HStack>
      </RadioGroup>

      {/* days counter */}
      <CounterField
        icon={CalendarIcon}
        label="Days"
        value={boatDays}
        dec={() => setBoatDays((n) => Math.max(1, n - 1))}
        inc={() => setBoatDays((n) => n + 1)}
      />

      {/* people counter */}
      <CounterField
        icon={FaUser}
        label="People"
        value={boatPeople}
        dec={() => setBoatPeople((n) => Math.max(1, n - 1))}
        inc={() => setBoatPeople((n) => n + 1)}
      />

      {/* Error message */}
      {error && (
        <Text color="red.500" mb={4} fontSize="sm">
          {error}
        </Text>
      )}

      <Divider mb={8} />

      <Button
        w="full"
        bg={BRAND}
        color="white"
        _hover={{ bg: BRAND }}
        borderRadius="lg"
        py={6}
        fontWeight="bold"
        onClick={handleCalculate}
        isLoading={loading}
      >
        <Translate text="Calculate" />
      </Button>
    </>
  );
};

export default BoatTripDetails;
